/**
 * Personenfilter je Projekt (TIM-18).
 *
 * Gespeichert wird die REGEL, nicht ihr Ergebnis. Die Kriterien (Projekte,
 * Zeitraum, "alle") verknuepfen sich als ODER; einzeln angeklickte Personen
 * kommen in `added` dazu. Ausgewertet wird bei jeder Anzeige neu — deshalb
 * erscheint eine frisch gebuchte Person sofort in ihrer Zeile, statt in einer
 * eingefrorenen Namensliste zu fehlen (TIM-17).
 *
 * Ein Gegenstueck zu `added` gibt es bewusst nicht: weicht die Auswahl von einem
 * Kriterium ab, wird das Kriterium aufgeloest und seine Personen wandern nach
 * `added`. Der Haken am Kaestchen verschwindet dabei, einen dritten Zustand gibt
 * es nicht (Hoffi am 11.08.).
 *
 * Der Zeitraum wird RELATIV zu heute abgelegt (Offset + Dauer). Absolute Daten
 * wuerden veralten: ein gespeichertes "diese Woche" zeigte naechste Woche noch
 * immer auf die vergangene.
 */
import { addDays, toIsoLocal, daysBetween, type IsoDate } from '@/utils/dateUtils'

export const FILTER_VERSION = 4

/**
 * Vorbelegung des Zeitraums: sechs Wochen einschliesslich heute (6 x 7 - 1).
 * Bewusst NICHT bis zum Projektende - der Filter kann sich auf mehrere Projekte
 * beziehen, ein einzelnes Projektende waere dafuer der falsche Bezug.
 */
export const DEFAULT_RANGE_DAYS = 41

/** Zeitraum, relativ zu heute. `days === null` = offenes Ende. */
export type Range = { fromOffset: number; days: number | null }

export type ResourceFilter = {
  v: number
  /** `auto` = nie angewendet, es gilt die Standardregel; `custom` = eigene Kriterien. */
  mode: 'auto' | 'custom'
  all: boolean
  projects: number[]
  /** Gilt fuer ALLE Kriterien, auch die Projekte. `null` = heute bis Projektende. */
  range: Range | null
  /**
   * Kriterium "Frei": im Zeitraum gar keine Buchung. Das einzige Kriterium, das
   * keinem Projekt entspricht — es ist die Verneinung jeder Zugehoerigkeit und
   * steht deshalb nirgends in der Datenbank. Wer Bereitschaft gemeldet hat, ist
   * damit NICHT frei, sondern ueber das Kaestchen von `*tbd` erreichbar.
   */
  free: boolean
  added: number[]
  /**
   * Sichtbare Kategorien der Unterprojekte (TIM-73). `null` = alle zeigen, was
   * auch der Standard ist; eine Liste blendet alles aus, was nicht darin steht.
   * Anders als die uebrigen Felder waehlt dieses keine Personen aus, sondern
   * Zeilen - es gilt deshalb unabhaengig von `mode`.
   */
  categories: string[] | null
}

/** Praefixe und Schreibweisen unterscheiden sich je Umgebung (`*tbd`, `>TBD`). */
function normalizeName(name: any): string {
  return String(name ?? '')
    .toLowerCase()
    .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
    .replace(/[^a-z]/g, '')
}

/**
 * Pseudo-Projekte, die keine Arbeit sind, sondern Verfuegbarkeit ausdruecken:
 * `*tbd` (arbeitswillig, noch keinem Projekt zugeordnet) und `*verfuegbar`.
 * Wer dort steht, gilt als verfuegbar, nicht als belegt.
 *
 * Erkannt wird am normalisierten Namen, weil die Schreibweise je Umgebung
 * abweicht und die IDs nicht garantiert stabil sind. Sauberer waere ein
 * Kennzeichen am Projekt — siehe TIM-45.
 */
const AVAILABILITY_PROJECTS = new Set(['tbd', 'verfugbar'])

export function isAvailabilityProject(p: any): boolean {
  return AVAILABILITY_PROJECTS.has(normalizeName(p?.name))
}

export function todayIso(): IsoDate {
  return toIsoLocal(new Date())
}

export function defaultFilter(): ResourceFilter {
  return { v: FILTER_VERSION, mode: 'auto', all: false, projects: [], range: null, free: false, added: [], categories: null }
}

/** Vorbelegung des Zeitraums: heute bis in sechs Wochen. */
export function defaultRange(today: IsoDate): { from: IsoDate; to: IsoDate } {
  return { from: today, to: addDays(today, DEFAULT_RANGE_DAYS) }
}

const asIdArray = (value: any): number[] =>
  Array.isArray(value) ? value.filter((n: any) => Number.isInteger(n)) : []

/**
 * Altbestand aus dem localStorage einlesen. Alles Unbekannte faellt bewusst auf
 * den Standard zurueck, damit nach einem Deploy niemand in einem halb gelesenen
 * Zustand landet.
 *
 * Altes Format: `{selection: 0|1|2, individual: number[]}` — 0 = im Projekt
 * gebucht, 1 = feste Namensliste, 2 = alle.
 */
export function migrateFilter(raw: any): ResourceFilter {
  if (raw == null || typeof raw !== 'object') return defaultFilter()

  if (raw.v === FILTER_VERSION) {
    const range = raw.range && Number.isFinite(raw.range.fromOffset)
      ? { fromOffset: raw.range.fromOffset, days: Number.isFinite(raw.range.days) ? raw.range.days : null }
      : null
    return {
      ...defaultFilter(),
      mode: raw.mode === 'custom' ? 'custom' : 'auto',
      all: raw.all === true,
      projects: asIdArray(raw.projects),
      range,
      free: raw.free === true,
      added: asIdArray(raw.added),
      categories: Array.isArray(raw.categories) ? raw.categories.filter((c: any) => typeof c === 'string') : null,
    }
  }

  if (raw.selection === 1) return { ...defaultFilter(), mode: 'custom', added: asIdArray(raw.individual) }
  if (raw.selection === 2) return { ...defaultFilter(), mode: 'custom', all: true }
  return defaultFilter()
}

/**
 * Ueberschneidet die Buchung den Zeitraum? ISO-Strings sind lexikografisch
 * vergleichbar, deshalb ist kein Date noetig. `to === null` = offenes Ende.
 */
export function overlapsRange(b: any, from: IsoDate, to: IsoDate | null): boolean {
  return (to === null || b.start <= to) && b.end >= from
}

/** In diesem Projekt gebucht, ohne Zeitbezug. */
export function bookedInProject(bookings: any[], projectId: number): number[] {
  const ids = new Set<number>()
  for (const b of bookings) if (b.project_id === projectId) ids.add(b.user_id)
  return [...ids]
}

/** In diesem Projekt gebucht, mit mindestens einem Tag innerhalb des Zeitraums. */
export function bookedInRange(bookings: any[], projectId: number, from: IsoDate, to: IsoDate | null): number[] {
  const ids = new Set<number>()
  for (const b of bookings) {
    if (b.project_id !== projectId) continue
    if (!overlapsRange(b, from, to)) continue
    ids.add(b.user_id)
  }
  return [...ids]
}

/**
 * Frei = im Zeitraum ueberhaupt keine Buchung. Das ist die Verneinung jeder
 * Projektzugehoerigkeit und steht deshalb, anders als alle anderen Kriterien,
 * nirgends in der Datenbank.
 *
 * Buchungen in `*tbd` oder `*verfuegbar` zaehlen dabei mit: wer Bereitschaft
 * gemeldet hat, ist nicht "frei", sondern ueber das Kaestchen des jeweiligen
 * Projekts erreichbar. Beides zusammen angehakt ergibt alle Verfuegbaren.
 */
export function freeInRange(users: any[], bookings: any[], from: IsoDate, to: IsoDate | null): number[] {
  const belegt = new Set<number>()
  for (const b of bookings) if (overlapsRange(b, from, to)) belegt.add(b.user_id)
  return users.filter((u: any) => !belegt.has(u.id)).map((u: any) => u.id)
}

/** Absoluter Zeitraum aus der relativ gespeicherten Angabe. */
export function rangeOf(range: Range, today: IsoDate): { from: IsoDate; to: IsoDate | null } {
  const from = addDays(today, range.fromOffset)
  return { from, to: range.days === null ? null : addDays(from, range.days) }
}

/** Zeitraum -> relativ gespeicherte Angabe. */
export function rangeFrom(from: IsoDate, to: IsoDate | null, today: IsoDate): Range {
  return {
    fromOffset: daysBetween(today, from),
    days: to === null ? null : daysBetween(from, to),
  }
}

/**
 * Welche Personen zeigt dieser Filter? Im Modus `auto` gilt die Standardregel
 * "in diesem Projekt gebucht ab heute" — das ist der Zustand, solange niemand
 * eigene Kriterien angewendet hat.
 */
export function evaluateFilter(
  filter: ResourceFilter,
  project: any,
  users: any[],
  bookings: any[],
  projects: any[],
  today: IsoDate = todayIso(),
): Set<number> {
  if (filter.mode === 'auto') {
    // Ein Hauptprojekt zeigt auch, wer in einem seiner Unterprojekte steht: es
    // ist die Klammer um den ganzen Baum (TIM-73). Ohne das fehlte oben, wer
    // nur in der Unterkunft gebucht ist - bei Leistungsbereichen fiel es nicht
    // auf, weil dort die abgeleitete Buchung ohnehin entsteht.
    const eigeneUndKinder = [project.id, ...projects.filter(p => p.parent_id === project.id).map(p => p.id)]
    const ids = eigeneUndKinder.flatMap(id => bookedInRange(bookings, id, today, null))

    // Ein beendetes Projekt hat ab heute niemanden mehr gebucht - aufgeklappt
    // erschien deshalb keine einzige Zeile, waehrend im Kopfbalken weiter die
    // Zahlen aus der Vergangenheit standen (TIM-70). In dem Fall die Personen
    // seines eigenen Zeitraums zeigen.
    //
    // Bewusst nur dieser Fall und nicht generell der angezeigte Zeitraum: der
    // umfasst mehrere Jahre, damit waere aus *ana eine Liste von 70 statt 9
    // Personen geworden.
    if (ids.length === 0 && project.end && project.end < today) {
      return new Set(eigeneUndKinder.flatMap(id => bookedInRange(bookings, id, project.start, project.end)))
    }

    return new Set(ids)
  }

  // Der Zeitraum gilt fuer alle Kriterien, auch fuer die Projekte.
  const { from, to } = filter.range ? rangeOf(filter.range, today) : defaultRange(today)

  const ids = new Set<number>()
  if (filter.all) for (const u of users) ids.add(u.id)
  for (const pid of filter.projects) for (const id of bookedInRange(bookings, pid, from, to)) ids.add(id)

  if (filter.free) for (const id of freeInRange(users, bookings, from, to)) ids.add(id)

  for (const id of filter.added) ids.add(id)
  return ids
}
